import { IField, IFormPage } from 'app/services/api5-service/api.interface';
import { combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { forEachConfig } from 'common/dynamic-form/utils/formly-configs';
import { getGroupRowGap } from 'common/dynamic-form/utils/get-group-indent';
import { appendFieldBaseOptions } from 'common/dynamic-form/wrappers/field-base/field-base.utils';
import { appendPageOptions } from 'common/dynamic-form/wrappers/page/page.utils';

import { GroupConfig } from './model';
import { GroupProps } from './model/group-props.interface';

import {
  ISPFieldConfig,
  ISPFieldType,
  ISPFieldWrapper,
  ISPFormState,
  ISPValidator,
} from '../../model';
import { FIELD_HIDDEN_CLASS } from '../../utils/class-fields';

/**
 * Check that group field should be hidden
 *
 * @param configs - group field config
 */
export function isGroupShouldBeHidden(configs: ISPFieldConfig[]): boolean {
  return configs.every(config => config.props.isHidden);
}

/**
 * Check that group field should be hidden
 *
 * @param configs
 */
export function isGroupShouldBeHidden$(
  configs: ISPFieldConfig[],
): Observable<boolean> {
  if (!configs.length) {
    // empty groups are should be hidden
    return of(true);
  }

  return combineLatest(
    configs.map(
      config => config.props.isHidden$ || of(Boolean(config.props.isHidden)),
    ),
  ).pipe(
    map(hiddenState => hiddenState.every(isHidden => isHidden)),
    distinctUntilChanged(),
  );
}

/**
 * Check that field/page group should be displayed
 *
 * @param fieldOrPage - field or page metadata
 * @param configs
 * @param state
 */
export function isGroupFieldShouldBeHidden(
  fieldOrPage: IField | IFormPage,
  configs: ISPFieldConfig[],
  state: ISPFormState,
): boolean {
  return (
    state.hiddenService.isHidden(fieldOrPage.$name) ||
    isGroupShouldBeHidden(configs)
  );
}

/**
 * Stream of field/page group displayed state
 *
 * @param fieldOrPage - field or page metadata
 * @param configs
 * @param state
 */
export function isGroupFieldShouldBeHidden$(
  fieldOrPage: IField | IFormPage,
  configs: ISPFieldConfig[],
  state: ISPFormState,
): Observable<boolean> {
  return combineLatest([
    isGroupShouldBeHidden$(configs),
    state.hiddenService.isHidden$(fieldOrPage.$name),
  ]).pipe(
    map(hiddenStates => hiddenStates.some(isHidden => isHidden)),
    distinctUntilChanged(),
  );
}

/**
 * Add passmatch validator function to group config
 *
 * @param config - group config
 */
function addPassMatchValidator(
  config: ISPFieldConfig<ISPFieldType.Group>,
): void {
  const hasPasswordConfirmationValidation = config.fieldGroup.some(
    field =>
      field.type === ISPFieldType.Password &&
      field.props.originalControl.$checkpasswd,
  );

  if (!hasPasswordConfirmationValidation) {
    return;
  }

  config.validators = {
    validation: [ISPValidator.PassMatch],
  };
}

/**
 * Get group field config for fields
 *
 * @param configs - fields configs
 * @param name
 */
export function getGroupConfig(
  configs: ISPFieldConfig[],
  name?: string,
): ISPFieldConfig<ISPFieldType.Group> | undefined {
  if (!configs.length) {
    return;
  }

  const props: GroupProps = {
    name,
    inlineGroup: false,
    isHidden: isGroupShouldBeHidden(configs),
    isHidden$: isGroupShouldBeHidden$(configs),
  };

  const config: ISPFieldConfig<ISPFieldType.Group> = {
    props,
    type: ISPFieldType.Group,
    fieldGroup: configs,
    expressions: {
      'props.isHidden': props.isHidden$,
      className: props.isHidden$.pipe(
        map(isHidden => (isHidden ? FIELD_HIDDEN_CLASS : '')),
      ),
    },
  };

  addPassMatchValidator(config);

  return config;
}

/**
 * Get group field config for inline field groups
 *
 * @param configs - fields to wrapp into group metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getInlineFieldGroupConfig(
  configs: ISPFieldConfig[],
  field: IField,
  state: ISPFormState,
): GroupConfig {
  forEachConfig(configs, config => {
    // @HACK remove all label related wrappers from fields, so only group wrapper will take control
    config.wrappers = config.wrappers?.filter(wrapper =>
      [ISPFieldWrapper.ValidationError].includes(wrapper),
    );
  });

  const props: GroupProps = {
    inlineGroup: true,
    isHidden: isGroupFieldShouldBeHidden(field, configs, state),
    isHidden$: isGroupFieldShouldBeHidden$(field, configs, state),
    isFullWidth: field.$fullwidth === 'yes',
    layoutPlace: configs[0].props.layoutPlace || 'content',
  };

  forEachConfig(configs, config => {
    if (config.wrappers?.includes(ISPFieldWrapper.ValidationError)) {
      config.props.validationPopupSidePriority = ['bottom', 'top'];
    }
  });

  const config: GroupConfig = {
    props,
    type: ISPFieldType.Group,
    fieldGroup: configs,
    wrappers: [ISPFieldWrapper.FieldBase],
    expressions: {
      'props.isHidden': props.isHidden$,
      className: props.isHidden$.pipe(
        map(isHidden => (isHidden ? FIELD_HIDDEN_CLASS : '')),
      ),
    },
  };

  appendFieldBaseOptions(config, field, state);

  if (configs.some(c => c.type === ISPFieldType.Skeleton)) {
    config.wrappers = [];
  }

  return config;
}

/**
 * Get group field config for page wrapper
 *
 * @param configs - fields to wrapp into group
 * @param state - dynamic form state
 * @param page - raw page metadata
 */
export function getPageGroupConfig(
  configs: ISPFieldConfig[],
  state: ISPFormState,
  page: IFormPage,
): ISPFieldConfig<ISPFieldType.Group> {
  const props: GroupProps = {
    inlineGroup: false,
    isHidden: isGroupFieldShouldBeHidden(page, configs, state),
    isHidden$: isGroupFieldShouldBeHidden$(page, configs, state),
  };

  const config: ISPFieldConfig<ISPFieldType.Group> = {
    props,
    type: ISPFieldType.Group,
    wrappers: [ISPFieldWrapper.Page],
    fieldGroup: configs,
    expressions: {
      'props.isHidden': props.isHidden$,
      className: props.isHidden$.pipe(
        map(isHidden => (isHidden ? FIELD_HIDDEN_CLASS : '')),
      ),
    },
  };

  addPassMatchValidator(config);

  // @TODO i.ablov remove appending wrapper configs somewhere above. This shouldn't happen in config itself
  appendPageOptions(config, state, page);

  if (page.$indent) {
    config.props.rowGap = getGroupRowGap(page.$indent);
  }

  return config;
}
